import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Avatar, Drawer, SearchInput } from "components";
import { useSearchChatMessages } from "controller/hooks/chat/useChatSearch";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import { memo, useCallback, useState } from "react";
import type { ChatSearchResultItem } from "shared/types/chatSearch";
import { getFullName } from "utilz/helpers/stringManipulator";
import { getHighlightSx, getSnippetSegments } from "./utils/highlightText";
import { stripHtml } from "./utils/stripHtml";

dayjs.extend(isToday);

interface ChatSearchDrawerProps {
  open: boolean;
  onClose: () => void;
  serviceType: "ticket" | "task";
  serviceId: string;
  threadId?: string;
  onResultClick: (chatId: string) => void;
}

export const ChatSearchDrawer = memo(function ChatSearchDrawer({
  open,
  onClose,
  serviceType,
  serviceId,
  threadId,
  onResultClick,
}: ChatSearchDrawerProps) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");

  const { data: searchResponse, isLoading } = useSearchChatMessages({
    serviceType,
    serviceId,
    query: searchQuery,
    threadId,
  });

  const results = searchResponse?.data ?? [];
  const hasQuery = searchQuery.length >= 2;

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setSearchQuery("");
    onClose();
  }, [onClose]);

  const handleResultClick = useCallback(
    (chatId: string) => {
      onResultClick(chatId);
    },
    [onResultClick],
  );

  const formatTimestamp = (dateStr: string): string => {
    const date = dayjs(dateStr);
    if (date.isToday()) {
      return `Today, ${date.format("h:mm A")}`;
    }
    return date.format("MMM D, YYYY h:mm A");
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title="Find in chat"
      headerStyle={{ border: "none" }}
      additionalHeaderComponent={
        <Box
          sx={{
            padding: "12px 24px",
            borderTop: `1px solid ${theme.palette.neutral["010"]}`,
          }}
        >
          <SearchInput
            name="chat-search"
            placeholder="Search in chat..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ width: "100%" }}
          />
        </Box>
      }
      footer={<></>}
    >
      <Stack direction="column" sx={{ height: "100%", width: "392px" }}>
        {isLoading && hasQuery && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%", p: "24px" }}
          >
            <CircularProgress size={28} />
          </Stack>
        )}

        {!isLoading && hasQuery && results.length === 0 && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%", p: "24px" }}
          >
            <Typography
              variant="body-medium"
              sx={{ color: theme.palette.lightBg.low }}
            >
              No results found
            </Typography>
          </Stack>
        )}

        {!isLoading &&
          results.map((result: ChatSearchResultItem) => (
            <SearchResultItem
              key={result.chat_id}
              result={result}
              query={searchQuery}
              onClick={handleResultClick}
              formatTimestamp={formatTimestamp}
            />
          ))}
      </Stack>
    </Drawer>
  );
});

// ─── Search Result Item ────────────────────────────────────────────

interface SearchResultItemProps {
  result: ChatSearchResultItem;
  query: string;
  onClick: (chatId: string) => void;
  formatTimestamp: (dateStr: string) => string;
}

const SearchResultItem = memo(function SearchResultItem({
  result,
  query,
  onClick,
  formatTimestamp,
}: SearchResultItemProps) {
  const theme = useTheme();
  const plainText = stripHtml(result.message);
  const segments = getSnippetSegments(plainText, query);
  const highlightSx = getHighlightSx(theme);

  return (
    <Stack
      direction="row"
      gap="12px"
      onClick={() => onClick(result.chat_id)}
      sx={{
        padding: "16px",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: theme.palette.neutral["005"],
        },
      }}
    >
      <Avatar
        fullname={getFullName(result.sender.first_name, null, result.sender.last_name)}
        url={result.sender.profile_pic}
      />
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          noWrap
          sx={{ color: theme.palette.lightBg.high }}
        >
          {getFullName(result.sender.first_name, null, result.sender.last_name)}
        </Typography>
        <Typography
          variant="body-medium"
          sx={{
            color: theme.palette.lightBg.medium,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {segments.map((segment, index) =>
            segment.isMatch ? (
              <Box key={index} component="span" sx={highlightSx}>
                {segment.text}
              </Box>
            ) : (
              <span key={index}>{segment.text}</span>
            ),
          )}
        </Typography>
        <Typography
          variant="body-small"
          sx={{
            color: theme.palette.lightBg.low,
            marginTop: "4px",
          }}
        >
          {formatTimestamp(result.created_at)}
        </Typography>
      </Stack>
    </Stack>
  );
});
